import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { useSelector } from "react-redux";
import { X } from "lucide-react";
import { RootState } from "../../state/store";
import CountryCodeDropDownAndPhoneField from "./countrycodedropdownandphonefield.tsx";
import Swal from "sweetalert2";

type loginModalProps = {
    show: boolean,
    onClose: () => void,
    onLoginSuccess: (token: string, role: string, name: string) => void,
    baseUrl: string,
    mode: string,
    setMode: () => void,
}

export function LoginModal({ show, onClose, onLoginSuccess, baseUrl, mode, setMode }: loginModalProps) {
    const [email, setEmailPhone] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const navigate = useNavigate(); // <-- add this
    const client = useSelector((state: RootState) => state.client.client);
    const client_uid = client?.uid || "clnt_QI9Jd1m0"; // fallback to default if not available
    const default_configs = client ? JSON.parse(client.default_configs) : {};

    
    const [identifier, setIdentifier] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [countryCode, setCountryCode] = useState({ code: "+47", country: "Norge", flag: "🇳🇴" });
    const [name, setName] = useState('');
    const [verificationCode, setVerificationCode] = useState('');
    const [customer, setCustomer] = useState({ token: '', role: '', name: '' });

    // Paste your VAPID public key here (base64 urlsafe)
    const VAPID_PUBLIC_KEY = 'BL1p6Elf7F9-UuePofmyrNqhN2G7j9fyLz5n1VDWMIxWn3Ptd6pBT-GSSi4corP1CT25rcm_TaMfVUSEoqYzoAs';

    const [status, setStatus] = useState('');



    function urlBase64ToUint8Array(base64String: any) {
        const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
        const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
        const rawData = window.atob(base64);
        const outputArray = new Uint8Array(rawData.length);
        for (let i = 0; i < rawData.length; ++i) {
            outputArray[i] = rawData.charCodeAt(i);
        }
        return outputArray;
    }

    useEffect(() => {
        async function askPermission() {
            const permission = await Notification.requestPermission();
            if (permission === "granted") {
                console.log("✅ Notifications allowed");
            } else {
                console.warn("🚫 Notifications not allowed:", permission);
            }
        }
        askPermission();
    }, []);

    // ESC key to close
    useEffect(() => {
        const handleEsc = (e: any) => {
            if (e.key === "Escape") onClose();
        };
        if (show) window.addEventListener("keydown", handleEsc);
        return () => window.removeEventListener("keydown", handleEsc);
    }, [show, onClose]);

    const VippsLogo: React.FC<{ className?: string }> = ({ className = "h-6" }) => (
        <svg className={className} viewBox="0 0 120 32" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" role="img">
            <title>Vipps</title>
            <text x="0" y="22" fontFamily="Inter, system-ui, Arial" fontSize="20" fontWeight="700" fill="#fff">
                vipps
            </text>
        </svg>
    );

    const handleLogin = async (e: any) => {
        e.preventDefault();
        setError("");
        setLoading(true);
        const formData = new FormData();
        formData.append("password", password);
        formData.append("client_uid", client_uid);
        setStatus("Triggering push...");
        if (mode === "login") {
            formData.append("email", email);
            try {
                const response = await axios.post(baseUrl + "/login", formData);


                if(response.data.status === "OK"){
                    console.log("Login success:", response.data);
                    setStatus('Push sent. Approve from your mobile browser.');
    
                    // call parent callback
                    if (typeof onLoginSuccess === "function") {
                        onLoginSuccess(response.data.token, response.data.role, response.data.name); // pass token and role
                    }
    
                    onClose(); // close modal on success
    
                    if (response.data.role === 'customer') {
                        navigate("/customer/dashboard"); // <-- redirect on success
                    }else if(response.data.role === 'support' || response.data.role === 'client'){
                        navigate("/support_admin/dashboard"); // <-- redirect on success
                    }else navigate("/agent/dashboard"); // <-- redirect on success
                }else {
                    Swal.fire({
                        title: 'Error',
                        icon: 'error',
                        text: response.data.data,
                    });
                    setError(response.data.data);
                }

            } catch (err: any) {
                console.error(err);
                setError(err.response?.data?.message || "Something went wrong!");
            } finally {
                setLoading(false);
            }
        } else if (mode === "register") {
            formData.append('email', identifier);
            formData.append('countryCode', countryCode.code);
            formData.append('phone', phoneNumber);
            formData.append('name', name);
            try {
                const response = await axios.post(baseUrl + '/registerCustomer', formData);

                console.log("Register");
                if (response.data.status === "OK") {
                    setCustomer({
                        token: response.data.token,
                        role: response.data.role,
                        name: response.data.name
                    });
                    setMode("otp_verification");
                } else if (response.data.status === "HAS_VIPPS") {
                    if (typeof onLoginSuccess === "function") {
                        onLoginSuccess(response.data.token, response.data.role, response.data.name); // pass token and role
                    }

                    window.location.href = response.data.vipps_link;
                }
            } catch (err) {
                console.error(err);
            }
        } else if (mode === 'otp_verification') {
            formData.append('verificationCode', verificationCode.toString());
            formData.append('token', customer?.token);
            try {
                const response = await axios.post(baseUrl + '/verifyOTP', formData);
                console.log("Verifying OTP");
                if (response.data.status === "OK") {
                    Swal.fire({
                        icon: "success",
                        title: response.data.data,
                        text: response.data.data,
                        showConfirmButton: true,
                    });
                    if (typeof onLoginSuccess === "function") {
                        onLoginSuccess(customer.token, customer.role, customer.name); // pass token and role
                    }
                } else {
                    let title = "";
                    if(response.data.status === "EXPIRED"){
                        title = "Timeout error";
                    }else if(response.data.status === "VERIFICATION_CODE_MISMATCH"){
                        title = "Verification code error";
                    }else {
                        title = response.data.status;
                    }
                    Swal.fire({
                        icon: "error",
                        title: title,
                        text: response.data.data,
                        showConfirmButton: true,
                    });
                }
            } catch (err: any) {
                console.error(err);
            }
        }
    };

    
    const resendOTP = async () => {
        setLoading(true);
        try {
            let formData = new FormData();
            formData.append("client_uid", client_uid);
            formData.append('token', customer?.token);
            const response = await axios.post(baseUrl + '/resendOTP', formData);
            
            if (response.data.status === "OK") {
                Swal.fire({
                    icon: "success",
                    title: "Logg inn godkjent",
                    text: response.data.message,
                    showConfirmButton: true,
                });

            } else {
                Swal.fire({
                    icon: "error",
                    title: "Vennligst prøv igjen",
                    text: response.data.message,
                    showConfirmButton: true,
                });
            }
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    }

    const titleMap = {
        login: `Logg inn på ${client.client_name}`,
        register: 'Opprett konto',
        reset: 'Tilbakestill passord',
    };

    const handleRegisterWithVipps = () => {
        if(mode === "register"){
            window.location.href = client ? client.vipps_link : "#";
        }else if(mode === "login"){
            window.location.href = client ? client.login_vipps_link : "#";
        }
    }

    return (
        <AnimatePresence>
            {show && (
                <motion.div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onClick={onClose}
                >
                    <motion.div
                        initial={{ scale: 0.9, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        exit={{ scale: 0.9, opacity: 0 }}
                        transition={{ duration: 0.25 }}
                        onClick={(e) => e.stopPropagation()}
                        role="dialog"
                        aria-modal="true"
                        className="relative bg-white rounded-2xl shadow-xl w-full max-w-md p-6"
                    >
                        <button
                            onClick={onClose}
                            aria-label="Lukk"
                            className="absolute top-4 right-4 text-gray-400 hover:text-gray-700 transition"
                        >
                            <X className="w-6 h-6" />
                        </button>

                        <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">{titleMap[mode]}</h2>

                        <form className="space-y-5" onSubmit={handleLogin}>
                            {/* Email for registration/reset */}
                            {(mode === 'otp_verification') && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Bekreftelseskode
                                    </label>
                                    <input type="text" required value={verificationCode} placeholder="Skriv inn bekreftelseskode" onChange={(e) => setVerificationCode(e.target.value)} className="w-full border border-gray-300 rounded-full px-4 py-2 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 outline-none text-gray-900 shadow-sm" />
                                </div>
                            )}
                            {(mode === 'register') && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Navn
                                    </label>
                                    <input type="text" required value={name} placeholder="Skriv inn navn" onChange={(e) => setName(e.target.value)} className="w-full border border-gray-300 rounded-full px-4 py-2 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 outline-none text-gray-900 shadow-sm" />
                                </div>
                            )}
                            {(mode === 'register' || mode === 'reset') && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        {mode === 'reset' ? 'E-post for gjenoppretting' : 'E-postadresse'}
                                    </label>
                                    <input
                                        value={identifier}
                                        onChange={(e) => setIdentifier(e.target.value)}
                                        type="email"
                                        placeholder="din@epost.no"
                                        className="w-full border border-gray-300 rounded-full px-4 py-2 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 outline-none text-gray-900 shadow-sm"
                                        required
                                    />
                                </div>
                            )}

                            {/* Identifier for login */}
                            {mode === 'login' && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">E-post eller telefon</label>

                                    <input
                                        value={email}
                                        onChange={(e) => setEmailPhone(e.target.value)}
                                        type="text"
                                        placeholder="E-post eller telefonnummer"
                                        className="w-full border border-gray-300 rounded-full px-4 py-2 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 outline-none text-gray-900 shadow-sm"
                                        required
                                    />
                                </div>
                            )}

                            {/* Password only for login (Vipps users don't need it) */}
                            {(mode === 'login' || mode === 'register') && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Passord</label>
                                    <input
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        type="password"
                                        placeholder="••••••••"
                                        className="w-full border border-gray-300 rounded-full px-4 py-2 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 outline-none text-gray-900 shadow-sm"
                                        required
                                        autoComplete="current-password"
                                    />
                                </div>
                            )}

                            {/* Vipps number (optional on register) */}
                            {mode === 'register' && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Vipps telefonnummer (valgfritt)</label>
                                    <CountryCodeDropDownAndPhoneField phoneNumber={phoneNumber} setPhoneNumber={setPhoneNumber} countryCode={countryCode} setCountryCode={setCountryCode} />
                                </div>
                            )}

                            <button
                                type="submit"
                                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 rounded-full shadow-md active:scale-[0.98] transition-all duration-150"
                            >
                                {mode === 'login' && (loading ? 'Logger inn...' : 'Logg inn')}
                                {mode === 'register' && (loading ? 'Oppretter konto...' : 'Opprett konto')}
                                {mode === 'reset' && (loading ? 'Sender lenke...' : 'Send tilbakestillingslenke')}
                                {mode === 'otp_verification' && (loading ? 'Bekrefter' : 'Verifisere')}
                            </button>



                            {default_configs.vipps_pay === "yes" && (
                                <>
                                    <div className="relative flex items-center justify-center my-5" aria-hidden="true">
                                        <span className="absolute bg-white px-3 text-gray-400 text-sm">eller</span>
                                        <div className="w-full h-px bg-gray-200" />
                                    </div>
                                    <button
                                        onClick={handleRegisterWithVipps}
                                        type="button"
                                        className="w-full flex items-center justify-center gap-2 bg-[#FF5B24] hover:bg-[#e24f20] text-white font-semibold py-2 rounded-full shadow-md active:scale-[0.98] transition-all duration-150"
                                        aria-label="Vipps"
                                    >
                                        <VippsLogo className="h-5" />
                                        {mode === 'login' && 'Logg inn med Vipps'}
                                        {mode === 'register' && 'Opprett konto med Vipps'}
                                        {mode === 'reset' && 'Tilbakestill via Vipps'}
                                    </button>
                                </>
                            )}

                        </form>

                        <div className="mt-6 text-center text-sm text-gray-600">
                            {mode === 'login' && (
                                <>
                                    <button onClick={() => setMode('register')} className="text-blue-600 hover:text-blue-700 font-medium">
                                        Opprett konto
                                    </button>
                                    <span className="mx-2">•</span>
                                    <button onClick={() => setMode('reset')} className="text-gray-600 hover:text-blue-700">
                                        Glemt passord?
                                    </button>
                                </>
                            )}
                            {(mode === 'register' || mode === 'reset') && (
                                <button onClick={() => setMode('login')} className="text-blue-600 hover:text-blue-700 font-medium">
                                    Tilbake til innlogging
                                </button>
                            )}
                            {(mode === 'otp_verification') && (
                                <button onClick={resendOTP} className="text-blue-600 hover:text-blue-700 font-medium">
                                    {loading ? 'Sender OTP på nytt...' : 'Send OTP på nytt'}
                                </button>
                            )}
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
